/**
 * OGC API Features client — replaces on-device GeoPackage / SpatiaLite queries.
 *
 * Speaks:
 *   - OGC API – Features Part 1 (Core):  bbox, limit, offset, datetime
 *   - OGC API – Features Part 3 (Filtering / CQL2):  filter, filter-lang, filter-crs
 *
 * All CQL2 filters are sent as CQL2-Text via `filter-lang=cql2-text`.
 */

import type { FeatureCollection, Feature } from 'geojson';
import type { FeatureEndpoints } from './OgcServicesContext';
import { or, ilike } from './cql2-filter-builder';
import { dwithin } from './cql2-spatial';
import { getAuthHeader } from './AuthContext';

// ─── Filter language enum ────────────────────────────────────────────────────

export type CqlFilterLang = 'cql2-text' | 'cql2-json';

// ─── Query params ────────────────────────────────────────────────────────────

export interface FeaturesQueryParams {
  /** Bounding box [west, south, east, north] */
  bbox?: [number, number, number, number];
  /** Max features returned (default 100) */
  limit?: number;
  /** Starting offset for pagination */
  offset?: number;
  /**
   * CQL2 filter expression.
   * Use the builder functions from cql2-filter-builder.ts or the aviation
   * presets from cql2-aviation-presets.ts to construct this string.
   */
  filter?: string;
  /**
   * Filter language. Defaults to 'cql2-text'.
   * Set to 'cql2-json' if sending a JSON-encoded CQL2 expression.
   */
  filterLang?: CqlFilterLang;
  /** CRS for the filter geometry literals (default: http://www.opengis.net/def/crs/OGC/1.3/CRS84) */
  filterCrs?: string;
  /** Property names to include in the response */
  properties?: string[];
  /** Sort by property(ies) — e.g. '+name' or '-elevation_ft' */
  sortby?: string;
  /** Datetime filter (ISO 8601 instant or interval: '2026-01-01/2026-12-31') */
  datetime?: string;
}

// ─── Client ──────────────────────────────────────────────────────────────────

export class OgcFeaturesClient {
  constructor(private endpoints: FeatureEndpoints) {}

  /**
   * Fetch a feature collection from an OGC API Features endpoint.
   *
   * When a `filter` is provided the client automatically sets
   * `filter-lang=cql2-text` (or cql2-json if overridden).
   */
  async getFeatures(
    collectionKey: keyof FeatureEndpoints,
    params?: FeaturesQueryParams,
  ): Promise<FeatureCollection> {
    const baseUrl = this.endpoints[collectionKey];
    const url = new URL(`${baseUrl}/items`, window.location.origin);

    // Core Part 1 params
    if (params?.bbox) url.searchParams.set('bbox', params.bbox.join(','));
    if (params?.limit) url.searchParams.set('limit', String(params.limit));
    if (params?.offset) url.searchParams.set('offset', String(params.offset));
    if (params?.datetime) url.searchParams.set('datetime', params.datetime);
    if (params?.properties) url.searchParams.set('properties', params.properties.join(','));
    if (params?.sortby) url.searchParams.set('sortby', params.sortby);

    // Part 3 CQL2 filtering
    if (params?.filter) {
      url.searchParams.set('filter', params.filter);
      url.searchParams.set('filter-lang', params.filterLang ?? 'cql2-text');
      if (params.filterCrs) {
        url.searchParams.set('filter-crs', params.filterCrs);
      }
    }

    url.searchParams.set('f', 'json');

    const res = await fetch(url.toString(), { headers: getAuthHeader() });
    if (!res.ok) throw new Error(`OGC Features error ${res.status}: ${res.statusText}`);
    return res.json() as Promise<FeatureCollection>;
  }

  /** Fetch a single feature by ID */
  async getFeature(
    collectionKey: keyof FeatureEndpoints,
    featureId: string,
  ): Promise<Feature> {
    const baseUrl = this.endpoints[collectionKey];
    const url = `${baseUrl}/items/${encodeURIComponent(featureId)}?f=json`;
    const res = await fetch(new URL(url, window.location.origin).toString(), { headers: getAuthHeader() });
    if (!res.ok) throw new Error(`OGC Features error ${res.status}: ${res.statusText}`);
    return res.json() as Promise<Feature>;
  }

  /**
   * Text search using CQL2 ILIKE across common name/ident fields.
   * Properly escapes the query string in a CQL2-Text expression.
   */
  async searchByText(
    collectionKey: keyof FeatureEndpoints,
    query: string,
    limit = 50,
  ): Promise<FeatureCollection> {
    const cql = or(
      ilike('name', `%${query}%`),
      ilike('ident', `%${query}%`),
    );
    return this.getFeatures(collectionKey, { filter: cql, limit });
  }

  /** Spatial search — features within a bounding box */
  async searchByBbox(
    collectionKey: keyof FeatureEndpoints,
    bbox: [number, number, number, number],
    limit = 200,
  ): Promise<FeatureCollection> {
    return this.getFeatures(collectionKey, { bbox, limit });
  }

  /**
   * Radius search using CQL2 S_INTERSECTS with a circle polygon.
   * More accurate than bbox approximation — sends actual spatial filter.
   */
  async searchByRadius(
    collectionKey: keyof FeatureEndpoints,
    center: { lat: number; lng: number },
    radiusNm: number,
    limit = 200,
  ): Promise<FeatureCollection> {
    const cql = dwithin('geometry', center.lng, center.lat, radiusNm);
    return this.getFeatures(collectionKey, { filter: cql, limit });
  }

  /**
   * Combined search: apply both bbox AND a CQL2 filter.
   * Useful for e.g. "airports in viewport that have jet fuel".
   */
  async searchByBboxWithFilter(
    collectionKey: keyof FeatureEndpoints,
    bbox: [number, number, number, number],
    cqlFilter: string,
    limit = 500,
  ): Promise<FeatureCollection> {
    return this.getFeatures(collectionKey, { bbox, filter: cqlFilter, limit });
  }

  /**
   * Paginated fetch with CQL2 filter.
   * Returns { features, hasMore, nextOffset }.
   */
  async getFilteredPage(
    collectionKey: keyof FeatureEndpoints,
    cqlFilter: string,
    offset = 0,
    limit = 100,
  ): Promise<{ features: FeatureCollection; hasMore: boolean; nextOffset: number }> {
    const fc = await this.getFeatures(collectionKey, { filter: cqlFilter, offset, limit: limit + 1 });
    const hasMore = fc.features.length > limit;
    if (hasMore) fc.features = fc.features.slice(0, limit);
    return { features: fc, hasMore, nextOffset: offset + limit };
  }
}
